import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Select from 'components/final-form/Select';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormLabelSpan from 'components/layout/PraxFormLabelSpan';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { FormApi } from 'final-form';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, RadioProps, Segment } from 'semantic-ui-react';
import axios from 'services/http';
import { getBillXmlRequests, updateRemoteStatus } from 'services/mediDataServices';
import styled from 'styled-components';
import { BillXmlStatus } from 'ts-types/api.enums';
import { BillXmlRequestDto, PatientDto, TariffDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledForm = styled.form`
  & .ui.grid > .row {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }

  & .ui.grid > .row:first-child {
    padding-bottom: 1rem !important;
  }

  & .ui.grid {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

const TableContainer = styled.div`
  min-height: 172px;
  max-width: 300px;
`;

const SearchSegment = styled(Segment)`

  &.ui.segment, .ui.segments &.segment {
    padding-left: 0;
    padding-bottom: 0;
  }
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onFilterBills: (tariffId: number, documents: Array<BillXmlRequestDto>) => void,
  onBillsLoadingChange: (documentsLoading: boolean) => void,
  billsLoading: boolean,
  currentPatient?: PatientDto,
  tariffs: Array<TariffDto>,
  onSetErrorMessages: (errorMessages: Array<string>) => void,
  errorMessages: Array<string>,
}

interface ExtendedBillXmlSearchRequest {
  mandateId: number;
  fromDate: string;
  toDate: string;
  tariffId?: number;
  filterByStatus: Array<BillXmlStatus>;
  filterByMediDataStatus: Array<string>;
}

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

class MediDataUploadSearchView extends Component<Props> {

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();
  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
  initialValues: Partial<ExtendedBillXmlSearchRequest> = {};

  constructor(props: Props) {
    super(props);
    this.initialValues = this.getInitialValues();
  }

  getInitialValues = (): Partial<ExtendedBillXmlSearchRequest> => {

    const {tariffs, currentUser} = this.props;

    const today = moment().format(ISO_DATE_FORMAT);

    return {
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined,
      fromDate: today,
      toDate: today,
      tariffId: (tariffs && tariffs.length > 0) ? tariffs[0].id : undefined,
      filterByStatus: Object.values(BillXmlStatus),
      filterByMediDataStatus: Object.values(["PROCESSING", "ERROR", "DONE"])
    }
  };

  handleChange = (values: Partial<ExtendedBillXmlSearchRequest>) => {

    const {onBillsLoadingChange} = this.props;

    onBillsLoadingChange(true);
    this.searchBills(values);
  };

  searchBills = (values: Partial<ExtendedBillXmlSearchRequest>) => {

    const {onBillsLoadingChange, onFilterBills} = this.props;

    const tariffId = values.tariffId as number;
    const startDate = moment(values.fromDate).format(ISO_DATE_FORMAT);
    const endDate = moment(values.toDate).format(ISO_DATE_FORMAT);
    const filterByStatus = values.filterByStatus && values.filterByStatus.length > 0
        ? values.filterByStatus
        : [];
    const mandateId = this.props.currentUser!.userConfig.mandateId;

    getBillXmlRequests(
        tariffId, startDate, endDate, filterByStatus, mandateId, this.cancelTokenSource)
        .then(response => {
          onFilterBills(tariffId, response);
        })
        .finally(() => {
          onBillsLoadingChange(false);
        })
  };

  updateRemoteStatus = async (values: Partial<ExtendedBillXmlSearchRequest>) => {
    const {currentUser, onBillsLoadingChange} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    onBillsLoadingChange(true);

    updateRemoteStatus(mandateId, this.cancelTokenSource)
        .then(response => {
          this.searchBills(values);
        })
        .catch((error: any) =>
            this.handleError(error.response.data))
        .finally(() => {
          onBillsLoadingChange(false);
        });
  };

  handleError = (error: any) => {
    const {getMessage} = this.props;
    if (error) {
      const errorCode = error.errorCode;
      const knownErrors: Array<string> = [
        errorUtils.medidataUpdateRemoteStatus
      ];
      const violations: Array<any> = error.violations;
      if (violations && violations.length > 0) {
        violations.forEach(violation => {
          if (knownErrors.includes(violation.errorCode)) {
            this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
          }
        });
      } else {
        if (knownErrors.includes(errorCode)) {
          this.setErrorMessage(getMessage(`error.${errorCode}`));
        } else {
          this.setErrorMessage(getMessage('error.general'));
        }
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    const {errorMessages, onSetErrorMessages} = this.props;
    if (errorMessage) {
      const errMsgs = [...errorMessages];
      errMsgs.push(errorMessage);
      onSetErrorMessages(errMsgs);
    } else {
      onSetErrorMessages([]);
    }
  };

  render(): React.ReactNode {
    return (
        <SearchSegment>
          <FinalForm
              onSubmit={(values, form) => {
                this.handleChange(values);
              }}
              initialValues={this.initialValues}
              render={this.renderForm}
          />
        </SearchSegment>
    );
  }

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const {
      getMessage,
      billsLoading,
      tariffs,
      errorMessages
    } = this.props;

    const tariffOptions: Array<DropdownOption> = tariffs.map(tariff => ({
      key: tariff.id,
      text: tariff.description,
      value: tariff.id
    }));

    const disableUpdateRemoteStatus = billsLoading;
    const disableSubmit = billsLoading || !(values.filterByStatus && values.filterByStatus.length > 0);

    return (
        <StyledForm
            onSubmit={handleSubmit}
        >
          <FormContainer>
            <FormContent>
              <PxGrid>
                {errorMessages.length > 0 &&
                    <Grid.Row>
                      <Grid.Column>
                        <StyledErrorMessage
                            onDismiss={() => this.setErrorMessage(undefined)}>
                          {errorMessages.map(err => <div key={err}>{err}</div>)}
                        </StyledErrorMessage>

                      </Grid.Column>
                    </Grid.Row>
                }
                <Grid.Row>
                  <Grid.Column width={13} verticalAlign="middle">
                    <Header>{getMessage("searchOptions")}</Header>
                  </Grid.Column>
                  <Grid.Column width={3} verticalAlign="middle" textAlign="right">
                    <Button primary
                            size="small"
                            compact
                            type="button"
                            disabled={disableUpdateRemoteStatus}
                            onClick={() => this.updateRemoteStatus(values)}>
                      {getMessage("mediData.updateRemoteStatusButton")}
                    </Button>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={4}>
                    <Grid>
                      <Grid.Row>
                        <Grid.Column width={2} verticalAlign="middle">
                          <PraxFormLabelSpan htmlFor="fromDate">{getMessage("from")}</PraxFormLabelSpan>
                        </Grid.Column>
                        <Grid.Column width={12}>
                          <Field
                              name="fromDate"
                              component={DatePicker}
                              fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={2} verticalAlign="middle">
                          <PraxFormLabelSpan htmlFor="toDate">{getMessage("to")}</PraxFormLabelSpan>
                        </Grid.Column>
                        <Grid.Column width={12}>
                          <Field
                              name="toDate"
                              component={DatePicker}
                              fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={2} verticalAlign="middle">
                          <PraxFormLabelSpan htmlFor="tariffId">{getMessage('tariff')}</PraxFormLabelSpan>
                        </Grid.Column>
                        <Grid.Column width={12}>
                          <Field
                              name="tariffId"
                              component={Select}
                              placeholder={`${getMessage('select')} ${getMessage('tariff')}`}
                              options={tariffOptions}
                              validate={value => {
                                return isNaN(value) ? "required" : undefined;
                              }}
                              fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Grid.Column width={16} verticalAlign="middle">
                      <PraxFormLabelSpan htmlFor="filterByStatus">
                        {getMessage('mediData.filterByStatus')}
                      </PraxFormLabelSpan>
                      <TableContainer>
                        {this.renderStatusesTable(form, values as ExtendedBillXmlSearchRequest)}
                      </TableContainer>
                    </Grid.Column>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Grid.Column width={16} verticalAlign="middle">
                      <PraxFormLabelSpan htmlFor="filterByStatus">
                        Filter by MediData Status
                      </PraxFormLabelSpan>
                      <TableContainer>
                        {this.renderMediDataStatusesTable(form, values as ExtendedBillXmlSearchRequest)}
                      </TableContainer>
                    </Grid.Column>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2} verticalAlign="bottom">
                    <Button primary size="tiny" compact
                            type="submit"
                            disabled={disableSubmit}
                    >
                      {getMessage("search")}
                    </Button>
                  </Grid.Column>
                </Grid.Row>
              </PxGrid>
            </FormContent>
          </FormContainer>
        </StyledForm>
    )
  }

  selectStatus = (values: ExtendedBillXmlSearchRequest, form: FormApi, status: BillXmlStatus) => {
    const statusSelected = values.filterByStatus.includes(status);
    if (statusSelected) {
      const statuses = values.filterByStatus.filter((s: BillXmlStatus) => s !== status);
      form.change("filterByStatus", statuses);
    } else {
      const statuses = [...values.filterByStatus, status];
      form.change("filterByStatus", statuses);
    }
  }

  selectHeaderColumn = (form: FormApi, values: ExtendedBillXmlSearchRequest, statuses: Array<BillXmlStatus>) => {

    const countSelectedStatuses = values.filterByStatus.length;
    const indeterminate = countSelectedStatuses > 0 && countSelectedStatuses < statuses.length;
    const noSelectedStatuses = countSelectedStatuses === 0 && statuses.length > 0;
    const allStatusesSelected = statuses.length > 0 && countSelectedStatuses === statuses.length;

    return (
        <CheckboxSc
            checked={countSelectedStatuses > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedStatuses || indeterminate) {
                  const selectedStatuses = statuses.map(status => status);
                  form.change("filterByStatus", selectedStatuses);
                }

                if (allStatusesSelected) {
                  form.change("filterByStatus", []);
                }

              }
            }
            disabled={statuses.length === 0}
        />
    )
  };

  statusesRowGetter = (statuses: Array<BillXmlStatus>) => ({index}: any) => {

    if (statuses[index]) {
      Object.assign(statuses[index], {index: index + 1});

      return statuses[index];
    }

    return {
      index: index + 1
    }
  };

  statusesRowRenderer = (form: FormApi, values: ExtendedBillXmlSearchRequest) => ({
                                                                                    className,
                                                                                    columns,
                                                                                    index,
                                                                                    key,
                                                                                    style,
                                                                                    rowData
                                                                                  }: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    let styles = {...style, userSelect: "none"};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.selectStatus(values, form, rowData)}
        >
          {columns}
        </div>
    );
  };

  statusSelectCellRenderer = (data: any, values: ExtendedBillXmlSearchRequest, form: FormApi) => {

    const {rowData} = data;

    const statusSelected = values.filterByStatus.includes(rowData);

    return (
        <CheckboxSc
            checked={statusSelected}
            onChange={() => this.selectStatus(values, form, rowData)}
        />
    )
  };

  statusDescriptionCellRenderer = (data: any) => {
    const {getMessage} = this.props;
    return <div>{getMessage(`transmissionStatus.${data.rowData}`)}</div>
  };

  renderStatusesTable = (form: FormApi, values: ExtendedBillXmlSearchRequest): React.ReactNode => {

    const {getMessage} = this.props;
    const billXmlStatuses = Object.values(BillXmlStatus);

    return (
        <StyledTable
            rowGetter={this.statusesRowGetter(billXmlStatuses)}
            rowRenderer={this.statusesRowRenderer(form, values)}
            rowCount={billXmlStatuses.length}
            columns={[
              {
                width: 30,
                label: this.selectHeaderColumn(form, values, billXmlStatuses),
                dataKey: "index",
                cellRenderer: (data: any) => this.statusSelectCellRenderer(data, values, form)
              },
              {
                width: 180,
                label: getMessage("description"),
                dataKey: "index",
                cellRenderer: this.statusDescriptionCellRenderer
              }
            ]}
        />
    )
  };

  renderMediDataStatusesTable = (
      form: FormApi, values: ExtendedBillXmlSearchRequest): React.ReactNode => {

    const {getMessage} = this.props;
    const billXmlStatuses = ["PROCESSING", "DONE", "ERROR"];

    return (
        <StyledTable
            rowGetter={this.mediDataStatusesRowGetter(billXmlStatuses)}
            rowRenderer={this.mediDataStatusesRowRenderer(form, values)}
            rowCount={billXmlStatuses.length}
            columns={[
              {
                width: 30,
                label: this.mediDataSelectHeaderColumn(form, values, billXmlStatuses),
                dataKey: "index",
                cellRenderer: (data: any) => this.mediDataStatusSelectCellRenderer(data, values, form)
              },
              {
                width: 180,
                label: getMessage("description"),
                dataKey: "index",
                cellRenderer: this.mediDataStatusDescriptionCellRenderer
              }
            ]}
        />
    )
  };

  mediDataStatusesRowGetter = (statuses: Array<string>) => ({index}: any) => {

    if (statuses[index]) {
      Object.assign(statuses[index], {index: index + 1});

      return statuses[index];
    }

    return {
      index: index + 1
    }
  };

  mediDataStatusesRowRenderer = (form: FormApi, values: ExtendedBillXmlSearchRequest) => ({
                                                                                            className,
                                                                                            columns,
                                                                                            index,
                                                                                            key,
                                                                                            style,
                                                                                            rowData
                                                                                          }: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    let styles = {...style, userSelect: "none"};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.selectMediDataStatus(values, form, rowData)}
        >
          {columns}
        </div>
    );
  };

  mediDataStatusDescriptionCellRenderer = (data: any) => {
    const {getMessage} = this.props;
    return <div>{getMessage(`${data.rowData}`)}</div>
  };

  mediDataStatusSelectCellRenderer = (data: any, values: ExtendedBillXmlSearchRequest, form: FormApi) => {

    const {rowData} = data;

    const statusSelected = values.filterByMediDataStatus.includes(rowData);

    return (
        <CheckboxSc
            checked={statusSelected}
            onChange={() => this.selectMediDataStatus(values, form, rowData)}
        />
    )
  };

  selectMediDataStatus = (values: ExtendedBillXmlSearchRequest, form: FormApi, status: string) => {
    const statusSelected = values.filterByMediDataStatus.includes(status);
    if (statusSelected) {
      const statuses = values.filterByMediDataStatus.filter((s: string) => s !== status);
      form.change("filterByMediDataStatus", statuses);
    } else {
      const statuses = [...values.filterByMediDataStatus, status];
      form.change("filterByMediDataStatus", statuses);
    }
  }

  mediDataSelectHeaderColumn = (
      form: FormApi, values: ExtendedBillXmlSearchRequest, statuses: Array<string>) => {

    const countSelectedStatuses = values.filterByMediDataStatus.length;
    const indeterminate = countSelectedStatuses > 0 && countSelectedStatuses < statuses.length;
    const noSelectedStatuses = countSelectedStatuses === 0 && statuses.length > 0;
    const allStatusesSelected = statuses.length > 0 && countSelectedStatuses === statuses.length;

    return (
        <CheckboxSc
            checked={countSelectedStatuses > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedStatuses || indeterminate) {
                  const selectedStatuses = statuses.map(status => status);
                  form.change("filterByMediDataStatus", selectedStatuses);
                }

                if (allStatusesSelected) {
                  form.change("filterByMediDataStatus", []);
                }

              }
            }
            disabled={statuses.length === 0}
        />
    )
  }
}

export default withMessages(withAuthContext(MediDataUploadSearchView));
